import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  MoreVertical,
  Trash2,
  FileText,
  FileSpreadsheet,
} from "lucide-react";
import toast from "react-hot-toast";

import Table from "../../table";
import Button from "../../button-v1";
import AlertDialog from "../../alert-dialogue";
import ShareModal from "../../shared-modules/share-modal";
import DateRangePicker from "../../shared-modules/DatePicker/range/index";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../ui/select";
import { Dropdown, DropdownItem } from "../../ui/dropdown";

import {
  getCategoryReport,
  getListReport,
  getExtendedThresholdReport,
  generateReport,
  removeReportPatient,
  postReportAuditEvent,
  markReportSubmitted,
  closeReport,
  closeAndSubmitReport,
  shareGeneratedReport,
  getGeneratedReport,
} from "../../../services/case-manager/oorReport";
import { getProviders } from "../../../services/case-manager/case-manager";
import {
  adaptCategoryRows,
  adaptListRows,
  adaptExtendedThresholdRows,
  collectVitalKeys,
  flattenListReadings,
} from "./adaptReport";
import VitalLegend from "./VitalLegend";
import ExtendedThresholdLegend, { ThresholdIcon } from "./ExtendedThresholdLegend";
import CategoryBar from "./CategoryBar";
import { ShareIcon, DownloadIcon } from "./icons";
import {
  useReportLifecycle,
  SUBMIT_MESSAGES,
  REPORT_STATUS,
} from "./ReportLifecycleContext";
import { MarkSubmittedDialog, CloseReportDialog } from "./LifecyclePopups";
import {
  generateCategoryReportPDF,
  generateListReportPDF,
  generateExtendedThresholdReportPDF,
  generateReadingsExcel,
} from "./exportReport";
import {
  REPORT_VIEWS,
  ET_HIGHLIGHTS,
  formatReportNumber,
  reportViewLabel,
} from "./reportViews";
import ReportHistory from "./ReportHistory";
import styles from "./styles.module.css";

const fmtReportDate = (d) =>
  d
    ? d.toLocaleDateString("en-US", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "";

const toYmd = (date) => {
  if (!date) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

// "2026-06-05" -> Date at local noon. Used to echo the backend auto-resolved
// range into the picker without a timezone shift. Noon matches the picker's
// own day dates, so its isInRange() doesn't also tint the start day.
const parseYmd = (ymd) => {
  if (!ymd || typeof ymd !== "string") return null;
  const [y, m, d] = ymd.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d, 12);
};

// SCRUM-918: default window on landing = the last 7 days including today,
// at local noon like parseYmd() so both ends render as selected.
const lastSevenDays = () => {
  const today = new Date();
  const endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 12);
  const startDate = new Date(endDate);
  startDate.setDate(startDate.getDate() - 6);
  return { startDate, endDate };
};

// The report-level fields kept next to the snapshot (SCRUM-790).
const pickReportInfo = (resp) =>
  resp
    ? {
        filters: resp.filters || resp.snapshot?.filters || null,
        reportNumber: resp.report_number ?? resp.snapshot?.report_number ?? null,
        reportNumberDisplay:
          resp.report_number_display ||
          resp.snapshot?.report_number_display ||
          null,
      }
    : null;

// DRF error bodies: {detail|error|status: "..."} or {field: "..."}; 5xx
// bodies are HTML and must never reach a toast.
const apiErrorMessage = (e) => {
  const data = e?.response?.data;
  if (!data || typeof data !== "object") return null;
  const first = data.detail || data.error || data.status || Object.values(data)[0];
  if (typeof first === "string") return first;
  if (Array.isArray(first) && typeof first[0] === "string") return first[0];
  return null;
};

// SCRUM-190 (in-memory delete; resets on refresh until the backend exclusion
// endpoint ships). Notes + provider filtering are now backend-owned.
const excludeDeleted = (rows, deletedIds) =>
  rows.filter((row) => !deletedIds.has(row.id));

/**
 * Lifecycle action buttons (SCRUM-191). Live in the My Tasks TAB ROW
 * (right-aligned, level with the tab triggers) per the Figma layout and read
 * the shared lifecycle context, so they stay in sync with the OOR panel:
 *   - idle               -> Generate report
 *   - generated          -> New report | Close Report | Mark as Submitted
 *   - submitted | closed -> New report
 *
 * The Mark Submitted and Close Report popups are rendered inside the OOR panel
 * (OORReport); these buttons just open them via the context.
 */
export const GenerateReportButton = () => {
  const {
    isIdle,
    isGenerated,
    generate,
    isGenerating,
    resetReport,
    openSubmitPrompt,
    openCloseReport,
    showHistory,
    submitBlocked,
    generateBlocked,
    reportView,
  } = useReportLifecycle();

  // SCRUM-138: the report lifecycle actions are hidden on the History screen
  // (Figma 1775-441 shows no tab-row buttons there).
  if (showHistory) return null;

  // SCRUM-790: only one Extended Threshold Draft can exist per location, so
  // while it's open it must be submitted or closed; no "New report".
  const hideNewReport = reportView === REPORT_VIEWS.EXTENDED_THRESHOLD;

  if (isIdle) {
    return (
      <Button
        variant="primary"
        className={`${styles.generateBtn} ${
          generateBlocked ? styles.generateBtnBlocked : ""
        }`}
        loading={isGenerating}
        // Same rule for all three reports: no records in the preview, nothing
        // to generate.
        disabled={isGenerating || generateBlocked}
        onClick={() => generate()}
      >
        Generate report
      </Button>
    );
  }

  if (isGenerated) {
    return (
      <div className={styles.lifecycleActions}>
        {!hideNewReport && (
          <Button
            variant="normal"
            className={`${styles.tabBtn} ${styles.tabBtnOutline}`}
            onClick={() => resetReport()}
          >
            New report
          </Button>
        )}
        <Button
          variant="normal"
          className={`${styles.tabBtn} ${styles.tabBtnDanger}`}
          onClick={() => openCloseReport()}
        >
          Close Report
        </Button>
        <Button
          variant="primary"
          className={`${styles.tabBtn} ${styles.tabBtnPrimary}`}
          disabled={submitBlocked}
          onClick={() => openSubmitPrompt()}
        >
          Mark as Submitted
        </Button>
      </div>
    );
  }

  // submitted | closed
  return (
    <Button
      variant="primary"
      className={styles.generateBtn}
      onClick={() => resetReport()}
    >
      New report
    </Button>
  );
};

/**
 * OOR Report (SCRUM-134): patient readings table with vital legend
 * (SCRUM-137) and provider filter (SCRUM-139), rendered inside the
 * My Tasks > Out of Range tab. Report lifecycle (SCRUM-191), report type
 * list view (SCRUM-188), row actions (SCRUM-189/190) and history (SCRUM-138)
 * land in their own stories; their controls are stubbed here.
 */
const OORReport = () => {
  // SCRUM-918: land on the last 7 days (including today). The echo ref starts
  // "applied" so the backend auto-resolved window doesn't replace this default;
  // clearing the range re-arms it (see handleRangeSelect).
  const [range, setRange] = useState(lastSevenDays);
  // Each report type keeps its own date range (Category and List don't
  // share picks); a type not visited yet starts on the last 7 days.
  const savedRangesRef = useRef({});
  const userPickedRangeRef = useRef(false);
  const echoAppliedRef = useRef(true);
  const [providerFilter, setProviderFilter] = useState(""); // provider ID ("" = all)
  const [reportType, setReportType] = useState(REPORT_VIEWS.CATEGORY);
  // SCRUM-790: the Extended Threshold Report is per location with a
  // backend-owned cycle, so it has no provider/date filters and no live
  // (pre-generate) query; it renders only from a generated report.
  const isET = reportType === REPORT_VIEWS.EXTENDED_THRESHOLD;

  const accId = sessionStorage.getItem("acc_id");
  const locationId = JSON.parse(
    sessionStorage.getItem("current_loc") || "{}"
  )?.id;

  const queryClient = useQueryClient();
  // SCRUM-190 delete row (in-memory; resets on refresh until the backend
  // exclusion endpoint ships with SCRUM-191).
  const [deletedIds, setDeletedIds] = useState(() => new Set());
  const [deleteTarget, setDeleteTarget] = useState(null);

  // SCRUM-191/192 report lifecycle. Status + the lifecycle popups are shared
  // with the tab-row buttons (GenerateReportButton) via context.
  const lifecycle = useReportLifecycle();
  const { isGenerated, isLocked, isFinal } = lifecycle;
  const [shareOpen, setShareOpen] = useState(false);

  // The locked snapshot returned by POST generate/ (same shape as the live
  // category/list responses). Non-null = the report renders from this, not
  // from the live queries. One generated report stores ONE view.
  const [snapshot, setSnapshot] = useState(null);
  // Report-level fields around the snapshot (Extended Threshold: filters with
  // the cycle dates, and report_number once submitted).
  const [reportInfo, setReportInfo] = useState(null);

  const startDate = toYmd(range.startDate);
  const endDate = toYmd(range.endDate);
  const hasRange = Boolean(startDate && endDate);

  // ── SCRUM-191: real Generate. The button lives in the tab row with no access
  // to this panel's filter state, so we register the API handler on the
  // lifecycle context and the button awaits it. Meta is captured at REQUEST
  // time (the old status-flip effect read a stale providerLabelRef).
  const runGenerate = async () => {
    try {
      // Extended Threshold: location + export type only (the backend ignores
      // provider and works out the cycle's date range itself).
      const resp = await generateReport(
        isET
          ? { location: locationId, reportView: reportType, exportType: "pdf" }
          : {
              location: locationId,
              provider: providerFilter || undefined,
              startDate: hasRange ? startDate : undefined,
              endDate: hasRange ? endDate : undefined,
              reportView: reportType,
              exportType: "pdf",
            }
      );
      const locName = JSON.parse(
        sessionStorage.getItem("current_loc") || "{}"
      )?.name;
      const accName = sessionStorage.getItem("acc_name") || "";
      // Prefer the backend-resolved dates (echoed even when we omitted them).
      const fStart = resp?.filters?.start_date;
      const fEnd = resp?.filters?.end_date;
      // The ET cycle dates must not leak into the other views' date picker.
      if (fStart && fEnd && !isET) {
        setRange({ startDate: parseYmd(fStart), endDate: parseYmd(fEnd) });
      }
      const meta = {
        accountName: [accName, locName].filter(Boolean).join(", ") || "—",
        providerLabel: isET
          ? "All Providers"
          : providerOptions.find((p) => p.id === providerFilter)?.name ||
            "All Providers",
        dateRangeLabel: `${fmtReportDate(
          fStart ? parseYmd(fStart) : range.startDate
        )} - ${fmtReportDate(fEnd ? parseYmd(fEnd) : range.endDate)}`,
        dateRangeStart: fStart || startDate,
        dateRangeEnd: fEnd || endDate,
        reportType,
      };
      setSnapshot(resp?.snapshot || null);
      setReportInfo(pickReportInfo(resp));
      // A regenerate is a brand-new Draft: removals from the old one don't apply.
      setDeletedIds(new Set());
      // The cached preview still says "no Draft".
      if (isET) dropEtPreview();
      return { reportId: resp.report_id, meta };
    } catch (e) {
      // Surface structured backend messages only. DRF validation errors come
      // as {field: "message"} or {error/detail: "..."}; 5xx bodies are HTML
      // error pages and must never reach the toast.
      const data = e?.response?.data;
      let beError = null;
      if (data && typeof data === "object") {
        const first = data.error || data.detail || Object.values(data)[0];
        if (typeof first === "string") beError = first;
        else if (Array.isArray(first) && typeof first[0] === "string")
          beError = first[0];
      }
      toast.error(
        beError ||
          "Could not generate the report. Please try again in a moment."
      );
      return null;
    }
  };
  const runGenerateRef = useRef(runGenerate);
  runGenerateRef.current = runGenerate;
  useEffect(() => {
    lifecycle.registerGenerateHandler(() => runGenerateRef.current());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The tab-row buttons hide "New report" for an Extended Threshold Draft.
  const { setReportView } = lifecycle;
  useEffect(() => {
    setReportView(reportType);
  }, [setReportView, reportType]);

  // The cached preview goes stale whenever its Draft is created, edited or
  // finalized; drop it so the next visit refetches instead of flashing the
  // old state (which could re-adopt a submitted Draft or offer Generate).
  const dropEtPreview = () =>
    queryClient.removeQueries(["oor-extended-threshold-report"], { exact: false });

  // SCRUM-790: show an existing Extended Threshold Draft in the generated
  // state (rows + Mark as Submitted / Close Report) without creating a new one.
  const adoptEtReport = ({ reportId, snapshot: snap, info }) => {
    const fStart = info?.filters?.start_date;
    const fEnd = info?.filters?.end_date;
    const locName = JSON.parse(
      sessionStorage.getItem("current_loc") || "{}"
    )?.name;
    const accName = sessionStorage.getItem("acc_name") || "";
    setSnapshot(snap || null);
    setReportInfo(info || null);
    setDeletedIds(new Set());
    setDeleteTarget(null);
    lifecycle.adoptReport({
      reportId,
      meta: {
        accountName: [accName, locName].filter(Boolean).join(", ") || "—",
        providerLabel: "All Providers",
        dateRangeLabel: `${fmtReportDate(parseYmd(fStart))} - ${fmtReportDate(
          parseYmd(fEnd)
        )}`,
        dateRangeStart: fStart,
        dateRangeEnd: fEnd,
        reportType: REPORT_VIEWS.EXTENDED_THRESHOLD,
      },
    });
  };

  // True while a generated Extended Threshold report is being loaded from
  // History; keeps the preview's own Draft from being adopted in between.
  const [openingDraft, setOpeningDraft] = useState(false);

  // Returning to idle ("New report") discards the old snapshot and the
  // report-scoped removals (a fresh report may legitimately include a
  // previously removed patient again).
  useEffect(() => {
    if (lifecycle.isIdle) {
      setSnapshot(null);
      setReportInfo(null);
      setDeletedIds(new Set());
      setDeleteTarget(null);
    }
  }, [lifecycle.isIdle]);

  // Provider sent by ID; send the date pair only when both are set (a single
  // date 400s; none -> backend auto-resolves the window).
  const reportParams = {
    startDate: hasRange ? startDate : undefined,
    endDate: hasRange ? endDate : undefined,
    provider: providerFilter || undefined,
  };
  const reportEnabled = Boolean(accId && locationId);

  // One query per active view (the inactive one is disabled). The backend owns
  // aggregation, the pending-OOR filter, previous-period data (dropped this
  // phase) and latest_note, so there's no client bootstrap / prev-window / N+1.
  // Live queries drive the IDLE (pre-generate) view only; once generated the
  // locked snapshot is the source of truth and the queries stand down.
  const categoryQuery = useQuery(
    ["oor-category-report", accId, locationId, startDate, endDate, providerFilter],
    () => getCategoryReport(reportParams),
    {
      enabled:
        reportEnabled && reportType === REPORT_VIEWS.CATEGORY && !snapshot,
    }
  );
  const listQuery = useQuery(
    ["oor-list-report", accId, locationId, startDate, endDate, providerFilter],
    () => getListReport(reportParams),
    { enabled: reportEnabled && reportType === REPORT_VIEWS.LIST && !snapshot }
  );

  // SCRUM-790: Extended Threshold preview of the location's current cycle.
  // Read-only (no Draft is saved), same object as a Draft's snapshot. The
  // report is per location with a backend-owned cycle, so the key carries no
  // provider/date filters.
  const etQuery = useQuery(
    ["oor-extended-threshold-report", accId, locationId],
    getExtendedThresholdReport,
    { enabled: reportEnabled && isET && !snapshot, retry: false }
  );

  const activeQuery = isET
    ? etQuery
    : reportType === REPORT_VIEWS.LIST
    ? listQuery
    : categoryQuery;
  const apiData = snapshot || activeQuery.data;
  const isLoading = !snapshot && (activeQuery.isLoading || openingDraft);

  // SCRUM-790: the preview returns the location's open Draft when there is
  // one. A Draft must be submitted (or closed) before another report can be
  // made, so open it straight in the generated state with its own report_id.
  const etPreview = etQuery.data;
  useEffect(() => {
    if (!isET || !lifecycle.isIdle || snapshot || openingDraft) return;
    if (etQuery.isFetching || !etPreview?.draft?.report_id) return;
    const { draft, ...previewSnapshot } = etPreview;
    adoptEtReport({
      reportId: draft.report_id,
      // The preview already leaves out the Draft's removed rows.
      snapshot: previewSnapshot,
      info: pickReportInfo({
        ...previewSnapshot,
        filters: draft.filters || previewSnapshot.filters,
      }),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isET, lifecycle.isIdle, snapshot, openingDraft, etQuery.isFetching, etPreview]);

  // Adopt the backend-resolved range into the picker once, on first load, while
  // the user hasn't picked a range. Null filters = no pending OOR readings.
  useEffect(() => {
    // The ET cycle dates must not leak into the other views' date picker.
    if (isET) return;
    if (userPickedRangeRef.current || echoAppliedRef.current) return;
    const f = apiData?.filters;
    if (f?.start_date && f?.end_date) {
      echoAppliedRef.current = true;
      setRange({
        startDate: parseYmd(f.start_date),
        endDate: parseYmd(f.end_date),
      });
    }
  }, [apiData, isET]);

  // Rows come straight from the adapters (notes/provider/pending-filter are
  // backend-owned); only the in-memory row delete is applied client-side.
  // Post-generate the snapshot feeds the ACTIVE view's adapter (a generated
  // report stores exactly one view, and the report-type select is locked).
  const rows = useMemo(
    () =>
      excludeDeleted(
        adaptCategoryRows(
          snapshot && reportType === REPORT_VIEWS.CATEGORY
            ? snapshot
            : categoryQuery.data
        ),
        deletedIds
      ),
    [categoryQuery.data, snapshot, reportType, deletedIds]
  );
  const listRows = useMemo(
    () =>
      excludeDeleted(
        adaptListRows(
          snapshot && reportType === REPORT_VIEWS.LIST ? snapshot : listQuery.data
        ),
        deletedIds
      ),
    [listQuery.data, snapshot, reportType, deletedIds]
  );
  // SCRUM-790: the preview before Generate, the saved Draft/report after.
  // Rows keep the backend's order (category severity, days, name).
  const etData = isET ? snapshot || etQuery.data : null;
  const etRows = useMemo(
    () => excludeDeleted(adaptExtendedThresholdRows(etData), deletedIds),
    [etData, deletedIds]
  );

  // An Extended Threshold draft needs at least one patient to be submitted.
  const { setSubmitBlocked, setGenerateBlocked } = lifecycle;
  useEffect(() => {
    setSubmitBlocked(isET && isGenerated && etRows.length === 0);
  }, [setSubmitBlocked, isET, isGenerated, etRows.length]);

  // SCRUM-139 provider options (value = provider ID). From the account
  // providers endpoint, de-duped with providers present in the report data.
  const { data: accountProviders } = useQuery(["oor-providers"], getProviders, {
    retry: false,
  });
  const providerOptions = useMemo(() => {
    const byId = new Map();
    (Array.isArray(accountProviders) ? accountProviders : []).forEach((p) => {
      const name = `${p.first_name || ""} ${p.last_name || ""}`.trim() || p.name;
      if (p.id != null && name) byId.set(String(p.id), name);
    });
    (apiData?.results || []).forEach((r) => {
      const prov = r.patient?.provider;
      if (prov?.id != null && prov?.name) byId.set(String(prov.id), prov.name);
    });
    return Array.from(byId, ([id, name]) => ({ id, name }));
  }, [accountProviders, apiData]);

  const activeRows = isET
    ? etRows
    : reportType === REPORT_VIEWS.LIST
    ? listRows
    : rows;

  // Same rule for all three reports: once the active report's preview has
  // loaded with no records, there's nothing to generate, so Generate report
  // is disabled.
  const previewEmpty =
    !snapshot && activeQuery.isSuccess && activeRows.length === 0;
  // Extended Threshold: never offer Generate while the preview is (re)loading
  // or already points at an open Draft (about to be adopted above); either
  // way a second Draft would be created.
  const etDraftPending =
    isET &&
    !snapshot &&
    (etQuery.isFetching || openingDraft || Boolean(etPreview?.draft));
  useEffect(() => {
    setGenerateBlocked(previewEmpty || etDraftPending);
  }, [setGenerateBlocked, previewEmpty, etDraftPending]);
  const sharePatients = useMemo(() => {
    const emailById = new Map();
    (apiData?.results || []).forEach((r) => {
      if (r.patient?.id != null) {
        emailById.set(
          r.patient.id,
          r.patient.email || r.patient.user?.email || ""
        );
      }
    });
    return activeRows.map((r) => ({
      id: r.id,
      name: r.patientName,
      email: emailById.get(r.id) || "",
    }));
  }, [activeRows, apiData]);

  // SCRUM-137: legend only lists vitals present in the rows currently shown
  // (follows the ACTIVE report type, per Mamoon's clarification on SCRUM-137).
  const vitalKeys = useMemo(() => collectVitalKeys(activeRows), [activeRows]);

  const handleRangeSelect = (startDate, endDate) => {
    // A real user pick locks out the backend auto-resolve echo; clearing the
    // range re-arms it so the next no-dates fetch can repopulate the picker.
    if (startDate && endDate) {
      userPickedRangeRef.current = true;
    } else {
      userPickedRangeRef.current = false;
      echoAppliedRef.current = false;
    }
    setRange({ startDate, endDate });
  };

  // ── SCRUM-190: delete row = report-scoped patient removal. Persisted via
  // POST <report>/remove-patient/ (generated reports only; the backend hides
  // the patient on that report, keeps the original snapshot, and logs its own
  // patient_removed audit event — no separate audit-event POST from here).
  const removePatientMutation = useMutation(
    ({ reportId, patientId }) => removeReportPatient(reportId, { patientId }),
    {
      onSuccess: (_data, { patientId, patientName }) => {
        // `already_removed: true` also lands here — treat as success.
        setDeletedIds((prev) => new Set(prev).add(patientId));
        setDeleteTarget(null);
        toast.success("Patient deleted");
        queryClient.invalidateQueries(["oor-report-history"], { exact: false });
        if (isET) dropEtPreview();
      },
      onError: (e) =>
        toast.error(apiErrorMessage(e) || "Could not remove the patient"),
    }
  );

  const confirmDelete = () => {
    if (!deleteTarget || removePatientMutation.isLoading) return;
    removePatientMutation.mutate({
      reportId: lifecycle.currentReportId,
      patientId: deleteTarget.id,
      patientName: deleteTarget.patientName,
    });
  };

  const handleRowAction = (value, row) => {
    if (value === "delete") setDeleteTarget(row);
  };

  // ── SCRUM-191/192: lifecycle actions (real backend) ─────────────────

  // Build the share file from the LOCKED snapshot rows via the Blob-mode
  // exporters. Formats are view-locked: a category report shares its category
  // PDF; a list report shares the list PDF or the readings Excel.
  const buildShareFile = (format) => {
    if (isET) return generateExtendedThresholdReportPDF({ ...etPdfArgs, asBlob: true });
    if (format === "excel") {
      const flat = flattenListReadings(snapshot).filter(
        (r) => !deletedIds.has(r.patientId)
      );
      return generateReadingsExcel({ rows: flat, accountName: reportAccountName, periodDate: endDate, asBlob: true });
    }
    const common = {
      accountName: reportAccountName,
      dateRange: reportDateRange,
      periodDate: endDate,
      providerLabel: reportProviderLabel,
      vitalKeys,
      asBlob: true,
    };
    return reportType === REPORT_VIEWS.LIST
      ? generateListReportPDF({ ...common, listRows })
      : generateCategoryReportPDF({ ...common, rows });
  };

  // The ShareModal emits recipient EMAILS (its Select values); the share
  // endpoint wants the recipient's system id. Resolve email -> id against the
  // provider caches the modal itself populated (same react-query keys).
  const resolveRecipient = (shareData) => {
    if (shareData.shareType === "patient") {
      const typed = (shareData.provider || "").trim().toLowerCase();
      const patient =
        (typed &&
          sharePatients.find(
            (p) => (p.email || "").trim().toLowerCase() === typed
          )) ||
        sharePatients.find(
          (p) => String(p.id) === String(shareData.patientId)
        );
      if (!patient) return null;
      const canonical = (patient.email || "").trim().toLowerCase();
      if (typed && typed !== canonical) return null;
      return {
        recipientType: "patient",
        recipientId: patient.id,
        recipientEmail: patient.email || undefined,
      };
    }
    if (shareData.externalProvider) {
      const list = queryClient.getQueryData(["external-providers"]) || [];
      const match = (Array.isArray(list) ? list : []).find(
        (p) => p.email === shareData.externalProvider
      );
      return match
        ? {
            recipientType: "external_provider",
            recipientId: match.id,
            recipientEmail: shareData.externalProvider,
          }
        : null;
    }
    if (shareData.provider) {
      const list = queryClient.getQueryData(["providers"]) || [];
      const match = (Array.isArray(list) ? list : []).find(
        (p) => p.email === shareData.provider
      );
      return match
        ? {
            recipientType: "provider",
            recipientId: match.id,
            recipientEmail: shareData.provider,
          }
        : null;
    }
    return null;
  };

  const shareMutation = useMutation(
    async (shareData) => {
      const recipient = resolveRecipient(shareData);
      if (!recipient?.recipientId) {
        // Free-typed emails can't be shared: the backend only emails
        // recipients it can resolve to a system record.
        const err = new Error("recipient-not-resolved");
        err.isRecipientError = true;
        throw err;
      }
      const format = shareData.format === "excel" ? "excel" : "pdf";
      const { blob, filename } = buildShareFile(format);
      return shareGeneratedReport(lifecycle.currentReportId, {
        blob,
        filename,
        format,
        ...recipient,
      });
    },
    {
      onSuccess: () => {
        setShareOpen(false);
        toast.success("Report shared");
        queryClient.invalidateQueries(["oor-report-history"], { exact: false });
        // A submitted Extended Threshold report can still be shared; there is
        // nothing left to submit then.
        if (!isGenerated) return;
        lifecycle.openSubmitPrompt({
          message: SUBMIT_MESSAGES.POST_SHARE,
          cancelLabel: "Not Submitted",
        });
      },
      onError: (e) => {
        if (e?.isRecipientError) {
          toast.error("Please select a recipient email from the dropdown to share the report");
          return;
        }
        if (e?.response?.status === 502) {
          toast.error("Email delivery failed. Please try again.");
          return;
        }
        toast.error(apiErrorMessage(e) || "Could not share the report");
      },
    }
  );

  const handleShare = (shareData) => shareMutation.mutate(shareData);

  // After a successful download: log the real `downloaded` audit event
  // (fire-and-forget — a logging hiccup must not disturb the download UX),
  // then offer to mark the report submitted.
  const afterDownload = (format) => {
    if (lifecycle.currentReportId) {
      postReportAuditEvent(lifecycle.currentReportId, {
        eventType: "downloaded",
        metadata: { format },
      })
        .then(() =>
          queryClient.invalidateQueries(["oor-report-history"], { exact: false })
        )
        .catch(() => {});
    }
    toast.success(`Report downloaded (${format.toUpperCase()})`);
    if (!isGenerated) return;
    lifecycle.openSubmitPrompt({
      message: SUBMIT_MESSAGES.POST_DOWNLOAD,
      cancelLabel: "Not Submitted",
    });
  };

  const downloadReport = async (format) => {
    // Client-side export of the LOCKED snapshot (Mamoon: Generate = lock,
    // Download = export to file). Formats are view-locked to the generated
    // view: a category report has no reading-level data for Excel, so Excel
    // is offered on list reports only (backend: one report per view).
    if (format === "excel") {
      try {
        const flat = flattenListReadings(snapshot).filter(
          (r) => !deletedIds.has(r.patientId)
        );
        generateReadingsExcel({ rows: flat, accountName: reportAccountName, periodDate: endDate });
      } catch (e) {
        toast.error("Could not generate the file");
        return;
      }
      afterDownload(format);
      return;
    }

    if (isET) {
      try {
        generateExtendedThresholdReportPDF(etPdfArgs);
      } catch (e) {
        toast.error("Could not generate the file");
        return;
      }
      afterDownload(format);
      return;
    }

    // PDF: vector jsPDF with embedded DM Sans, drawn at exact coordinates so
    // the typeface, widths and dot/text alignment match the Figma exactly.
    try {
      const common = {
        accountName: reportAccountName,
        dateRange: reportDateRange,
        periodDate: endDate,
        providerLabel: reportProviderLabel,
        vitalKeys,
      };
      if (reportType === REPORT_VIEWS.LIST) {
        generateListReportPDF({ ...common, listRows });
      } else {
        generateCategoryReportPDF({ ...common, rows });
      }
    } catch (e) {
      toast.error("Could not generate the file");
      return;
    }
    afterDownload(format);
  };

  // Terminal transitions persist server-side FIRST; the UI state machine only
  // advances on success (errors keep the report generated and editable).
  // Each endpoint logs its own audit event — no audit-event POSTs here.
  const finalizeMutation = useMutation(
    ({ action, note }) => {
      const reportId = lifecycle.currentReportId;
      if (action === "submit") return markReportSubmitted(reportId, { note });
      if (action === "close-and-submit")
        return closeAndSubmitReport(reportId, { note });
      return closeReport(reportId, { note });
    },
    {
      onSuccess: (data, { action, note }) => {
        if (isET && action !== "close") {
          refreshSubmittedReport(lifecycle.currentReportId, data);
        }
        if (action === "submit") {
          lifecycle.markSubmitted();
          lifecycle.dismissSubmitPrompt();
          toast.success("Report marked as submitted");
        } else if (action === "close-and-submit") {
          lifecycle.close({ alsoSubmit: true });
          lifecycle.dismissCloseReport();
          toast.success("Report closed and marked as submitted");
        } else {
          lifecycle.close();
          lifecycle.dismissCloseReport();
          toast.success("Report closed");
        }
        queryClient.invalidateQueries(["oor-report-history"], { exact: false });
        // A submit closes the ET cycle and a close discards the Draft; either
        // way the next preview must start fresh (and not re-adopt this Draft).
        if (isET) dropEtPreview();
      },
      onError: (e, { action }) => {
        const message = apiErrorMessage(e);
        // Stale ET draft: another report was submitted for this location
        // after this one was generated. Offer a fresh Draft.
        if (isET && action !== "close" && /regenerate/i.test(message || "")) {
          lifecycle.dismissSubmitPrompt();
          lifecycle.dismissCloseReport();
          showRegenerateToast(message);
          return;
        }
        toast.error(
          message ||
            (action === "close"
              ? "Could not close the report"
              : "Could not mark the report as submitted")
        );
      },
    }
  );

  // SCRUM-790: the report number exists only once submitted, so re-read the
  // detail to render the final PDF with it. The submit response's number and
  // submission date cover the gap if the detail call fails.
  const refreshSubmittedReport = async (reportId, submitResp) => {
    setReportInfo((prev) => ({
      ...prev,
      reportNumber: submitResp?.report_number ?? prev?.reportNumber ?? null,
      reportNumberDisplay:
        submitResp?.report_number_display || prev?.reportNumberDisplay || null,
    }));
    if (submitResp?.date_end) {
      setSnapshot((prev) =>
        prev
          ? { ...prev, filters: { ...prev.filters, end_date: submitResp.date_end } }
          : prev
      );
    }
    try {
      const detail = await getGeneratedReport(reportId);
      if (detail?.snapshot) setSnapshot(detail.snapshot);
      setReportInfo(pickReportInfo(detail));
    } catch (e) {
      // Keep the submit-response values; the PDF still gets its number.
    }
  };

  const showRegenerateToast = (message) =>
    toast.error(
      (t) => (
        <span className={styles.toastWithAction}>
          <span>{message}</span>
          <button
            type="button"
            className={styles.toastAction}
            onClick={() => {
              toast.dismiss(t.id);
              lifecycle.generate();
            }}
          >
            Regenerate
          </button>
        </span>
      ),
      { duration: 10000 }
    );

  const NO_PATIENTS_MESSAGE =
    "An Extended Threshold Report needs at least one patient to be submitted.";
  const blockEmptyEtSubmit = () => {
    if (!(isET && etRows.length === 0)) return false;
    toast.error(NO_PATIENTS_MESSAGE);
    return true;
  };

  // Mark Submitted prompt (direct action + post-share / post-download).
  const handleSubmitConfirm = (note) => {
    if (blockEmptyEtSubmit()) return;
    finalizeMutation.mutate({ action: "submit", note });
  };

  // Close Report dialog.
  const handleCloseWithoutSubmitting = (note) =>
    finalizeMutation.mutate({ action: "close", note });

  const handleCloseAndSubmit = (note) => {
    if (blockEmptyEtSubmit()) return;
    finalizeMutation.mutate({ action: "close-and-submit", note });
  };

  // Name / Provider / Action are identical in both report types
  // (sorting only on Name and Provider, per Devon Gaur on SCRUM-134).
  const sharedColumns = useMemo(
    () => ({
      name: {
        key: "patientName",
        label: "Patient Name",
        alignLeft: true,
        render: (row) => (
          <div>
            <div className={styles.nameText}>{row.patientName}</div>
            <div className={styles.dobText}>{row.dob}</div>
          </div>
        ),
      },
      provider: {
        key: "providerName",
        label: "Provider",
        alignLeft: true,
        render: (row) => (
          <span className={styles.providerText}>{row.providerName || "--"}</span>
        ),
      },
      action: {
        key: "action",
        label: "Action",
        sorting: false,
        // Delete is only available on a GENERATED report (business ruling
        // 2026-07-07): not before Generate, and not on submitted/closed
        // (read-only) reports.
        render: (row) =>
          !isGenerated || isFinal ? null : (
          <Dropdown
            align="right"
            className={styles.actionMenu}
            // The table's scroll wrapper clips an in-flow menu on the last
            // rows; the portal renders it above everything and flips it up
            // when there's no room below.
            usePortal
            onChange={(value) => handleRowAction(value, row)}
            trigger={
              <button
                type="button"
                className={styles.menuBtn}
                onClick={(e) => e.stopPropagation()}
              >
                <MoreVertical size={16} />
              </button>
            }
          >
            <DropdownItem
              value="delete"
              className={`${styles.actionMenuItem} ${styles.actionMenuDanger}`}
            >
              <Trash2 size={16} />
              <span>Delete row</span>
            </DropdownItem>
          </Dropdown>
        ),
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isFinal, isGenerated]
  );

  const categoryColumns = useMemo(
    () => [
      sharedColumns.name,
      sharedColumns.provider,
      {
        key: "readings",
        label: "No. of Readings",
        sorting: false,
        alignLeft: true,
        render: (row) => (
          <div className={styles.vitalStack}>
            {row.vitals.map((vital) => (
              <div
                key={vital.key}
                className={`${styles.vitalGroup} ${styles.readingsGroup}`}
              >
                <div className={styles.vitalLabel}>{vital.label}</div>
                <div className={styles.readingsMeta}>
                  {vital.total} Total readings
                </div>
                <div className={styles.readingsMeta}>
                  {vital.outOfRange} Out of range
                </div>
              </div>
            ))}
          </div>
        ),
      },
      {
        key: "category",
        label: "Category",
        sorting: false,
        alignLeft: true,
        render: (row) => (
          <div className={styles.vitalStack}>
            {row.vitals.map((vital) => (
              <div
                key={vital.key}
                className={`${styles.vitalGroup} ${styles.categoryGroup}`}
              >
                <div className={styles.vitalLabel}>{vital.label}</div>
                <CategoryBar vital={vital} />
              </div>
            ))}
          </div>
        ),
      },
      sharedColumns.action,
    ],
    [sharedColumns]
  );

  // SCRUM-188 List Report: individual escalated-reading rows per vital,
  // 26px zebra rows that visually continue across the No. of Readings and
  // Category cells (the stripes use the same indices/heights in both).
  const listColumns = useMemo(
    () => [
      sharedColumns.name,
      sharedColumns.provider,
      {
        key: "readings",
        label: "No. of Readings",
        sorting: false,
        alignLeft: true,
        render: (row) => (
          <div className={`${styles.listStack} ${styles.listStackReadings}`}>
            {row.vitals.map((vital) => {
              // One grid template per GROUP: if any reading in this vital
              // carries a bpm pair, every row gets the bpm column (possibly
              // empty) so the date column never shifts between rows.
              const hasBpm = vital.readings.some((r) => r.secondary);
              return (
                <div key={vital.key}>
                  <div className={styles.listVitalLabel}>{vital.label}</div>
                  <div className={styles.listRows}>
                    {vital.readings.map((item) => (
                      <div
                        key={item.id}
                        className={`${styles.listRow} ${styles.listReadingRow} ${
                          hasBpm ? styles.withBpm : ""
                        }`}
                      >
                        <span className={styles.listCell}>
                          <span
                            className={styles.listDot}
                            style={{ backgroundColor: item.color }}
                          />
                          <span className={styles.listValue}>{item.value}</span>
                        </span>
                        {hasBpm && (
                          <span className={styles.listCell}>
                            {item.secondary && (
                              <>
                                <span
                                  className={styles.listDot}
                                  style={{
                                    backgroundColor: item.secondary.color,
                                  }}
                                />
                                <span className={styles.listValue}>
                                  {item.secondary.value}
                                </span>
                              </>
                            )}
                          </span>
                        )}
                        <span className={styles.listDate}>{item.dateText}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ),
      },
      {
        key: "category",
        label: "Category",
        sorting: false,
        alignLeft: true,
        render: (row) => (
          <div className={`${styles.listStack} ${styles.listStackCats}`}>
            {row.vitals.map((vital) => (
              <div key={vital.key}>
                {/* invisible spacer keeps these rows aligned with the
                    vital-heading offset in the readings cell */}
                <div
                  className={`${styles.listVitalLabel} ${styles.labelSpacer}`}
                >
                  {vital.label}
                </div>
                <div className={styles.listRows}>
                  {vital.readings.map((item) => (
                    <div key={item.id} className={styles.listRow}>
                      {item.categories.map((cat, idx) => (
                        <span key={idx} className={styles.listCatPair}>
                          <span
                            className={styles.listDot}
                            style={{ backgroundColor: cat.color }}
                          />
                          <span className={styles.listCatLabel}>
                            {cat.label}
                          </span>
                        </span>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ),
      },
      sharedColumns.action,
    ],
    [sharedColumns]
  );

  // SCRUM-790 Extended Threshold (Figma 7119-798): Name | DOB | Reason |
  // Action. The whole row is tinted by the API's `highlight` (rowColor).
  const etColumns = useMemo(
    () => [
      {
        key: "patientName",
        label: "Patient Name",
        alignLeft: true,
        sorting: false,
        render: (row) => <span className={styles.etCellText}>{row.patientName}</span>,
      },
      {
        key: "dob",
        label: "DOB",
        alignLeft: true,
        sorting: false,
        render: (row) => <span className={styles.etCellText}>{row.dob || "--"}</span>,
      },
      {
        key: "reason",
        label: "Reason",
        alignLeft: true,
        sorting: false,
        // `reason` is backend-generated; rendered as-is.
        render: (row) => (
          <span className={styles.etReason}>
            <ThresholdIcon category={row.category} />
            <span>{row.reason}</span>
          </span>
        ),
      },
      sharedColumns.action,
    ],
    [sharedColumns]
  );
  const etRowColor = (row) => ET_HIGHLIGHTS[row.highlight]?.row || "transparent";
  // The shared Table's hover repaints every cell grey over the row color, so
  // highlighted rows get their own hover (a darker shade of their color).
  const ET_ROW_HOVER_CLASS = {
    yellow: styles.etRowYellow,
    orange: styles.etRowOrange,
    red: styles.etRowRed,
  };
  const etRowClassName = (row) => ET_ROW_HOVER_CLASS[row.highlight] || "";

  // Header values shared by the on-page filters and the hidden print layout.
  const reportAccountName = (() => {
    const locName = JSON.parse(
      sessionStorage.getItem("current_loc") || "{}"
    )?.name;
    const accName = sessionStorage.getItem("acc_name") || "";
    return [accName, locName].filter(Boolean).join(", ") || "—";
  })();
  // providerFilter holds the provider ID; resolve it back to a name for the
  // report header / PDF exports.
  const reportProviderLabel =
    providerOptions.find((p) => p.id === providerFilter)?.name ||
    "All Providers";
  const reportDateRange = `${fmtReportDate(range.startDate)} - ${fmtReportDate(
    range.endDate
  )}`;

  // Extended Threshold PDF inputs, all from the report itself: the cycle
  // dates from the snapshot (end = submission date once submitted) and the
  // number, which only a submitted report has.
  const etReportNumber = formatReportNumber(
    reportInfo?.reportNumber,
    reportInfo?.reportNumberDisplay
  );
  const etPdfArgs = {
    accountName: reportAccountName,
    reportNumberDisplay: etReportNumber,
    startDate: snapshot?.filters?.start_date || reportInfo?.filters?.start_date,
    endDate: snapshot?.filters?.end_date || reportInfo?.filters?.end_date,
    thresholdKey: snapshot?.threshold_key,
    highlightKey: snapshot?.highlight_key,
    rows: etRows,
  };
  // ET share/download stay available after submit: the final, numbered PDF
  // is rendered from the submitted report.
  const canExport =
    isGenerated || (isET && lifecycle.status === REPORT_STATUS.SUBMITTED);

  // An open Extended Threshold Draft has no "New report", so the report type
  // stays switchable: leaving only unloads it here (it stays open on the
  // backend and is picked up again when Extended Threshold is reselected).
  const etDraftOpen = isET && isGenerated;
  const reportTypeLocked = isLocked && !etDraftOpen;
  // Remember the current type's range and bring back the next type's own
  // (last 7 days if it hasn't been opened yet, SCRUM-918). Extended
  // Threshold has no date filter, so its slot is never used.
  const switchRangeTo = (nextType) => {
    savedRangesRef.current[reportType] = range;
    const saved = savedRangesRef.current[nextType];
    userPickedRangeRef.current = Boolean(saved);
    echoAppliedRef.current = true;
    setRange(saved || lastSevenDays());
  };

  const handleReportTypeChange = (value) => {
    if (value === reportType) return;
    if (etDraftOpen) {
      lifecycle.resetReport();
      dropEtPreview();
    }
    switchRangeTo(value);
    setReportType(value);
  };

  // SCRUM-790: "Open this Report" on a generated (not yet submitted) Extended
  // Threshold report brings it back here, in the generated state, so it can
  // be submitted. Submitted/closed reports keep opening as a PDF.
  const openEtDraftFromHistory = async (report) => {
    const reportId = report.report_id ?? report.id;
    lifecycle.closeHistory();
    if (etDraftOpen && lifecycle.currentReportId === reportId) return;
    setOpeningDraft(true);
    if (!lifecycle.isIdle) lifecycle.resetReport();
    if (reportType !== REPORT_VIEWS.EXTENDED_THRESHOLD) {
      switchRangeTo(REPORT_VIEWS.EXTENDED_THRESHOLD);
    }
    setReportType(REPORT_VIEWS.EXTENDED_THRESHOLD);
    try {
      const detail = await getGeneratedReport(reportId);
      if (detail?.status !== REPORT_STATUS.GENERATED) {
        toast.error("This report is no longer open for submission.");
        queryClient.invalidateQueries(["oor-report-history"], { exact: false });
        return;
      }
      adoptEtReport({
        reportId,
        snapshot: detail.snapshot,
        info: pickReportInfo(detail),
      });
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Could not open this report.");
    } finally {
      setOpeningDraft(false);
    }
  };

  // SCRUM-138: in-place History view. The OORReport stays mounted (its filters,
  // date range, report type and lifecycle state persist), so the back arrow
  // returns to the exact state the user left.
  if (lifecycle.showHistory) {
    return (
      <ReportHistory
        onBack={lifecycle.closeHistory}
        onOpenDraft={openEtDraftFromHistory}
      />
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.filtersRow}>
        <div className={styles.filtersLeft}>
          {/* SCRUM-790: the Extended Threshold cycle and scope are
              backend-owned (per location), so no date/provider pickers. */}
          {isET ? (
            etReportNumber ? (
              <span className={styles.etReportNumber}>
                Report #{etReportNumber}
              </span>
            ) : null
          ) : (
            <>
              <div className={styles.dateRangeWrap}>
                <DateRangePicker
                  startDate={range.startDate}
                  endDate={range.endDate}
                  onRangeSelect={handleRangeSelect}
                  placeholder="Select Date Range"
                  disabled={isLocked}
                />
              </div>

              {/* Filters lock once a report is generated (SCRUM-191): the report
                  is a snapshot of these inputs. */}
              <div
                className={`${styles.providerSelect} ${
                  isLocked ? styles.filterLocked : ""
                }`}
              >
                <Select
                  value={providerFilter}
                  onChange={setProviderFilter}
                  disabled={isLocked}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Provider">
                      {providerFilter
                        ? providerOptions.find((p) => p.id === providerFilter)
                            ?.name || "Provider"
                        : "Provider"}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent searchField={true}>
                    <SelectItem value="">All Providers</SelectItem>
                    {providerOptions.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
        </div>

        <div className={styles.filtersRight}>
          <div
            className={`${styles.reportTypeSelect} ${
              reportTypeLocked ? styles.filterLocked : ""
            }`}
          >
            <Select
              value={reportType}
              onChange={handleReportTypeChange}
              disabled={reportTypeLocked}
            >
              <SelectTrigger>
                <SelectValue placeholder="Category Report">
                  {reportViewLabel(reportType)}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={REPORT_VIEWS.CATEGORY}>
                  {reportViewLabel(REPORT_VIEWS.CATEGORY)}
                </SelectItem>
                <SelectItem value={REPORT_VIEWS.LIST}>
                  {reportViewLabel(REPORT_VIEWS.LIST)}
                </SelectItem>
                <SelectItem value={REPORT_VIEWS.EXTENDED_THRESHOLD}>
                  {reportViewLabel(REPORT_VIEWS.EXTENDED_THRESHOLD)}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Share / Download appear only once a report is generated
              (SCRUM-191/192). Share opens the reusable ShareModal; Download
              opens a PDF/Excel menu. */}
          {canExport && (
            <>
              <button
                type="button"
                className={styles.iconBtn}
                aria-label="Share report"
                onClick={() => setShareOpen(true)}
              >
                <ShareIcon />
              </button>
              <Dropdown
                align="right"
                className={styles.downloadMenu}
                onChange={(value) => downloadReport(value)}
                trigger={
                  <button
                    type="button"
                    className={styles.iconBtn}
                    aria-label="Download report"
                  >
                    <DownloadIcon />
                  </button>
                }
              >
                <DropdownItem value="pdf" className={styles.downloadMenuItem}>
                  <FileText size={16} />
                  <span>Download as PDF</span>
                </DropdownItem>
                {/* One generated report stores ONE view; only the list
                    snapshot carries reading-level data, so Excel is
                    view-locked to list reports. */}
                {reportType === REPORT_VIEWS.LIST ? (
                  <DropdownItem value="excel" className={styles.downloadMenuItem}>
                    <FileSpreadsheet size={16} />
                    <span>Download as Excel</span>
                  </DropdownItem>
                ) : null}
              </Dropdown>
            </>
          )}

          <Button
            variant="normal"
            className={styles.historyBtn}
            onClick={() => lifecycle.openHistory()}
          >
            View report history
          </Button>
        </div>
      </div>

      {isET ? (
        <ExtendedThresholdLegend
          thresholdKey={etData?.threshold_key}
          highlightKey={etData?.highlight_key}
        />
      ) : (
        <VitalLegend vitalKeys={vitalKeys} />
      )}

      {isET ? (
        !etData && etQuery.isError ? (
          <div className={styles.etEmpty}>
            Could not load the Extended Threshold preview. You can still
            generate the report to see this cycle's patients.
          </div>
        ) : etData && etRows.length === 0 ? (
          <div className={styles.etEmpty}>
            No patients qualify for the Extended Threshold Report this cycle.
          </div>
        ) : (
          <div className={`${styles.tableWrap} ${styles.etMode}`}>
            <Table
              columns={etColumns}
              data={etRows}
              loading={isLoading}
              rowColor={etRowColor}
              getRowClassName={etRowClassName}
              primaryHeader
            />
          </div>
        )
      ) : reportType === REPORT_VIEWS.LIST ? (
        <div className={`${styles.tableWrap} ${styles.listMode}`}>
          <Table
            columns={listColumns}
            data={listRows}
            loading={isLoading}
            primaryHeader
          />
        </div>
      ) : (
        <div className={styles.tableWrap}>
          <Table
            columns={categoryColumns}
            data={rows}
            loading={isLoading}
            primaryHeader
          />
        </div>
      )}

      {/* SCRUM-190: delete-row confirmation (standard Toca AlertDialog,
          per Mamoon). */}
      <AlertDialog
        open={!!deleteTarget}
        setOpen={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        title="Delete Row"
        description={
          <>
            Are you sure you want to remove{" "}
            <strong>"{deleteTarget?.patientName}"</strong> from the report?
          </>
        }
      >
        <Button variant="normal" onClick={() => setDeleteTarget(null)}>
          Cancel
        </Button>
        <Button variant="danger" onClick={confirmDelete}>
          Yes, Remove
        </Button>
      </AlertDialog>

      {/* SCRUM-192: Share the generated report. Reuses the shared ShareModal
          with the File type selector enabled for OOR only (PDF / Excel). */}
      <ShareModal
        isOpen={shareOpen}
        setIsOpen={setShareOpen}
        title="Share"
        onShare={handleShare}
        isSharing={shareMutation.isLoading}
        customPatients={sharePatients}
        showFileTypeSelector
        availableFormats={
          reportType === REPORT_VIEWS.LIST ? ["pdf", "excel"] : ["pdf"]
        }
        defaultFormat="pdf"
      />

      {/* SCRUM-191/192: Mark Submitted prompt — direct "Mark as Submitted"
          plus the post-share / post-download follow-ups (driven by context). */}
      <MarkSubmittedDialog
        open={lifecycle.submitPrompt.open}
        setOpen={(open) => {
          if (!open) lifecycle.dismissSubmitPrompt();
        }}
        message={lifecycle.submitPrompt.message}
        cancelLabel={lifecycle.submitPrompt.cancelLabel}
        onConfirm={handleSubmitConfirm}
        onCancel={lifecycle.dismissSubmitPrompt}
      />

      {/* SCRUM-191: Close Report confirm (close, or close & mark submitted). */}
      <CloseReportDialog
        open={lifecycle.closeReportOpen}
        setOpen={(open) => {
          if (!open) lifecycle.dismissCloseReport();
        }}
        onCloseWithoutSubmitting={handleCloseWithoutSubmitting}
        onCloseAndSubmit={handleCloseAndSubmit}
      />
    </div>
  );
};

export default OORReport;
